import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Zap, Clock, DollarSign } from "@/components/icons";
import { fetchUnit } from "@/lib/api";
import type { ActiveSession } from "@/lib/api";
import { formatNum } from "@/lib/unit-helpers";
import { Badge } from "@/components/ui/badge";

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatEnergy(wh: number): string {
  if (!wh) return "0.00";
  return (wh / 1000).toFixed(2);
}

interface SessionDetailPanelProps {
  session: ActiveSession;
}

export function SessionDetailPanel({ session: s }: SessionDetailPanelProps) {
  const unitQ = useQuery({
    queryKey: ["unit", s.unit_id],
    queryFn: () => fetchUnit(s.unit_id),
    enabled: !!s.unit_id,
    refetchInterval: 5_000,
  });

  const unit = unitQ.data;
  const online = unit?.is_online !== false;

  return (
    <div className="space-y-5">
      {/* Session header */}
      <div>
        <div className="flex items-center gap-3">
          <Zap className="h-5 w-5 text-success" />
          <h2 className="text-xl font-bold">{s.unit_name || s.unit_id}</h2>
        </div>
        <div className="font-mono text-sm opacity-60 mt-1">{s.unit_id}</div>
        <div className="mt-2">
          <Badge variant="success">Charging</Badge>
        </div>
      </div>

      {/* Session metrics */}
      <div className="grid grid-cols-2 gap-3">
        <DetailItem label="Energy Delivered" value={`${formatEnergy(s.energy_delivered)} kWh`} />
        <DetailItem label="Duration" value={formatDuration(s.duration_seconds)} />
        <DetailItem label="Total Cost" value={`$${(s.costs?.total_cost ?? 0).toFixed(2)}`} />
        <DetailItem label="Subsessions" value={String(s.subsessions_count ?? 0)} />
        <DetailItem label="Started" value={s.start_time ? new Date(s.start_time).toLocaleString() : "—"} />
      </div>

      {/* Cost breakdown */}
      {s.costs && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold opacity-70 uppercase">Cost Breakdown</h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailItem label="Activation Fee" value={`$${(s.costs.activation_fee ?? 0).toFixed(2)}`} />
            <DetailItem label="Energy Cost" value={`$${(s.costs.energy_cost ?? 0).toFixed(2)}`} />
            <DetailItem label="Time Cost" value={`$${(s.costs.time_cost ?? 0).toFixed(2)}`} />
            <DetailItem label="Idle Cost" value={`$${(s.costs.idle_cost ?? 0).toFixed(2)}`} />
          </div>
        </div>
      )}

      {/* Unit telemetry (if loaded) */}
      {unit && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold opacity-70 uppercase">Live Telemetry</h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailItem label="Status" value={online ? unit.state_name || "unknown" : "offline"} />
            <DetailItem label="Voltage" value={`${formatNum(unit.voltage, 1)}V`} />
            <DetailItem label="Current" value={`${formatNum(unit.current, 1)}A`} />
            <DetailItem label="Power" value={`${formatNum(unit.power_kw, 2)} kW`} />
            <DetailItem label="Power Factor" value={formatNum(unit.power_factor, 2)} />
          </div>
        </div>
      )}

      {/* Link to full detail */}
      <div className="pt-2 border-t border-border">
        <Link
          to={`/unit/${encodeURIComponent(s.unit_id)}`}
          className="text-sm font-medium text-primary hover:underline"
        >
          View Full Unit Details →
        </Link>
      </div>
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card rounded-lg p-3">
      <div className="text-xs opacity-60">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
